import { type ReactNode, useRef, useState } from 'react';

import { cva } from 'cva';
import { Button as RACButton } from 'react-aria-components/Button';
import {
  type Key,
  Select,
  type SelectProps,
  SelectValue,
  type ValidationResult,
} from 'react-aria-components/Select';

import { ClearButton } from '../clear_button/clear_button';
import { FieldDescription } from '../form/field_description';
import { FieldError } from '../form/field_error';
import { FieldLabel } from '../form/field_label';
import { Icon, type IconProps } from '../icon/icon';
import { DropdownList } from '../list_box/dropdown_list';
import type { DropdownListOption } from '../list_box/dropdown_list_content';
import {
  SearchableDropdownList,
  type SearchableDropdownListProps,
} from '../list_box/searchable_dropdown_list';

import styles from './select_field.module.css';

const selectFieldVariants = cva({
  base: styles.select_field,
  variants: {
    size: {
      s: styles.size_s,
      m: styles.size_m,
      l: styles.size_l,
    },
    readOnly: {
      true: styles.read_only,
    },
  },
  defaultVariants: {
    size: 'm',
  },
});

export type SelectFieldOption = DropdownListOption;

export type SelectFieldProps = Omit<
  SelectProps<SelectFieldOption>,
  | 'children'
  | 'selectionMode'
  | 'selectedKey'
  | 'defaultSelectedKey'
  | 'onSelectionChange'
> & {
  label?: string;
  isReadOnly?: boolean;
  isRequired?: boolean;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  options: SelectFieldOption[];
  placeholder?: string;
  searchFieldLabel?: string;
  optionsListLabel?: string;
  noResultsText?: string;
  loadingText?: string;
  clearSelectionLabel?: string;
  isSearchable?: boolean;
  size?: 's' | 'm' | 'l';
  value?: Key | null;
  defaultValue?: Key | null;
  onChange?: (value: Key | null) => void;
  isClearable?: boolean;
  isLoading?: boolean;
  onLoadMore?: () => void;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  filter?: SearchableDropdownListProps['filter'];
  renderOption?: (option: SelectFieldOption) => ReactNode;
  /**
   * Renders the selected option in the trigger. Falls back to the option label
   * when not provided, or when the selected option is not in `options`.
   */
  renderValue?: (option: SelectFieldOption) => ReactNode;
};

type SelectFieldSize = NonNullable<SelectFieldProps['size']>;
const iconSizeMap: Record<SelectFieldSize, IconProps['size']> = {
  s: 'xs',
  m: 's',
  l: 's',
};

/**
 * Wrapper for a searchable single-select field built on top of react-aria Select.
 */
export function SelectField({
  className,
  size = 'm',
  isReadOnly,
  isDisabled,
  isRequired,
  label,
  description,
  errorMessage,
  options,
  placeholder = 'Select an option',
  searchFieldLabel = 'Search options',
  optionsListLabel = 'Options',
  noResultsText = 'No results found',
  loadingText = 'Loading options…',
  clearSelectionLabel = 'Clear selection',
  isSearchable = true,
  value,
  onChange,
  onOpenChange,
  defaultValue,
  isClearable,
  isLoading,
  onLoadMore,
  searchValue: controlledSearchValue,
  onSearchValueChange,
  filter,
  renderOption,
  renderValue,
  ...rest
}: SelectFieldProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const isAsyncSearch = onSearchValueChange !== undefined;

  const [localSearchValue, setLocalSearchValue] = useState('');
  const [innerValue, setInnerValue] = useState<Key | null>(
    defaultValue ?? null,
  );
  const selectedValue = value !== undefined ? value : innerValue;
  const hasSelectedValue = selectedValue !== null;
  const canClearSelection = Boolean(
    isClearable && hasSelectedValue && !isReadOnly && !isDisabled,
  );

  const selectedOption = renderValue
    ? options.find((option) => option.id === selectedValue)
    : undefined;

  const classes = selectFieldVariants({
    size,
    readOnly: isReadOnly,
    className,
  });

  const handleChange = (nextValue: Key | null) => {
    setLocalSearchValue('');
    setInnerValue(nextValue);
    onChange?.(nextValue);
  };

  const handleClearSelection = () => {
    handleChange(null);
    triggerRef.current?.focus();
  };

  return (
    <Select<SelectFieldOption>
      allowsEmptyCollection
      {...rest}
      className={classes}
      placeholder={placeholder}
      value={selectedValue}
      onChange={handleChange}
      onOpenChange={(isOpen) => {
        if (!isOpen && !isAsyncSearch) {
          setLocalSearchValue('');
        }
        onOpenChange?.(isOpen);
      }}
      isRequired={isRequired}
      isDisabled={isDisabled}
    >
      {label ? <FieldLabel isRequired={isRequired}>{label}</FieldLabel> : null}
      <div className={styles.trigger_wrapper}>
        {canClearSelection ? (
          <ClearButton
            slot={null}
            iconSize={iconSizeMap[size]}
            onPress={handleClearSelection}
            className={styles.clear_button}
            aria-label={clearSelectionLabel}
          />
        ) : null}
        <RACButton
          ref={triggerRef}
          className={styles.trigger}
          isDisabled={isDisabled || isReadOnly}
        >
          <SelectValue<SelectFieldOption> className={styles.value}>
            {({ selectedText }) =>
              selectedOption && renderValue
                ? renderValue(selectedOption)
                : selectedText || placeholder
            }
          </SelectValue>
          {!isReadOnly ? (
            <Icon
              name={'chevron_small_isolated_s'}
              size={size}
              className={styles.icon}
            />
          ) : null}
        </RACButton>
      </div>

      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <FieldError>{errorMessage}</FieldError>

      {isSearchable ? (
        <SearchableDropdownList
          triggerRef={triggerRef}
          options={options}
          selectionMode="single"
          matchTriggerWidth
          size={size}
          searchValue={
            onSearchValueChange
              ? (controlledSearchValue ?? '')
              : localSearchValue
          }
          onSearchValueChange={onSearchValueChange ?? setLocalSearchValue}
          searchFieldLabel={searchFieldLabel}
          optionsListLabel={optionsListLabel}
          noResultsText={noResultsText}
          loadingText={loadingText}
          isLoading={isLoading}
          onLoadMore={onLoadMore}
          filter={filter}
          renderOption={renderOption}
        />
      ) : (
        <DropdownList
          triggerRef={triggerRef}
          options={options}
          selectionMode="single"
          matchTriggerWidth
          size={size}
          optionsListLabel={optionsListLabel}
          noResultsText={noResultsText}
          loadingText={loadingText}
          renderOption={renderOption}
        />
      )}
    </Select>
  );
}
